import {
  useClient,
  useUpdateClient,
  useCreateInvestmentAccount,
  useInvalidateClients,
} from "creso-core/hooks";
import { UpdateClientSchema } from "creso-core/zod";
import {
  Button,
  cn,
  DialogFooter,
  Form,
  FormField,
  FormInput,
  FormPrefixedInput,
} from "web-design-system";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { handleError } from "@/utils/error";
import { toast } from "sonner";
import { useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { Avatar, ClientStatusBadge } from "web-core";
import { getInitials } from "creso-core/utils";
import { Activity, useCallback, useState } from "react";
import { InfoIcon, Loader2Icon } from "lucide-react";
import { useCheckKyc } from "@crm/hooks/onboarding";
import { AddClientDialogActions } from "./add-client-dialog";
import { KycDescription, KycStatusBadge } from "./kyc-status";

export function ClientLeadAccountUpdateForm({
  id,
  action,
  onClose,
}: {
  action?: AddClientDialogActions;
  id: number | string;
  onClose?: () => void;
}) {
  const { data: client } = useClient(Number(id));

  const router = useRouter();
  const navigate = useNavigate();
  const location = useLocation();

  const [cachedClientName, setCachedClientName] = useState<string | null>(null);

  const form = useForm({
    values: {
      name: client?.name ?? "",
      pan: client?.pan ?? "",
      email: client?.email ?? "",
      phone_number: client?.phone_number ?? "",
    },
    resolver: zodResolver(UpdateClientSchema),
  });

  const updateClient = useUpdateClient(Number(id));

  const { mutateAsync: createInvestmentAccount } = useCreateInvestmentAccount();

  const [checkKyc, isCheckingKyc] = useCheckKyc(Number(id));

  const handleCheckKyc = useCallback(
    async (data: z.infer<typeof UpdateClientSchema>) => {
      try {
        setCachedClientName(client?.name || null);
        await checkKyc(data);
      } catch {
        toast.error("Failed to check KYC status");
      }
    },
    [checkKyc, client?.name]
  );

  const { dirtyFields, isSubmitting } = form.formState;

  const panIsDirty = !!dirtyFields.pan;
  const panValue = form.watch("pan");

  const invalidateClients = useInvalidateClients();

  const createUpdateAccount = async (
    data: z.infer<typeof UpdateClientSchema>
  ) => {
    try {
      const updated = await updateClient.mutateAsync(data);
      if (action === "create-account") {
        await createInvestmentAccount(updated.id);
        await router.invalidate();
        invalidateClients();
        const portfolioPath = `/clients/${updated.id}/portfolio`;
        if (location.pathname !== portfolioPath) {
          navigate({
            to: "/clients/$id/portfolio",
            params: { id: updated.id.toString() },
            search: {},
          });
        }
      } else {
        toast.success("Updated client details");
      }
      onClose?.();
    } catch (error) {
      handleError(
        error,
        action === "create-account"
          ? "Failed to create account"
          : "Failed to update client"
      );
    }
  };

  const shouldCheckKyc =
    !client?.kyc_status ||
    client?.kyc_status === "U" ||
    panIsDirty ||
    !panValue ||
    isCheckingKyc;
  const showKycStatus = !shouldCheckKyc;
  const nameChanged =
    showKycStatus &&
    !!cachedClientName &&
    !!client?.name &&
    cachedClientName !== client.name;

  return (
    <div className="flex flex-col size-full gap-6 p-6">
      <h4>
        {action === "create-account"
          ? "Create account"
          : "Update client details"}
      </h4>
      <Form {...form}>
        <form className="flex flex-col flex-1 gap-4">
          <Activity mode={action !== "create-account" ? "visible" : "hidden"}>
            <FormField
              name="phone_number"
              render={({ field }) => (
                <FormPrefixedInput
                  placeholder="9999999999"
                  prefix="+91"
                  label="Phone number"
                  {...field}
                >
                  +91
                </FormPrefixedInput>
              )}
            />
            <FormField
              name="name"
              render={({ field }) => (
                <FormInput placeholder="Enter name" label="Name" {...field} />
              )}
            />
          </Activity>
          {action === "create-account" ? (
            <div className="rounded-lg p-3 border border-border flex items-center gap-2">
              <Avatar fallback={getInitials(client?.name || "")} size="sm" />
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-semibold">{client?.name}</span>
                {client?.phone_number && (
                  <span className="text-xs text-muted-foreground">
                    {client.phone_number}
                  </span>
                )}
              </div>
              <ClientStatusBadge status={client?.status ?? "LEAD"} />
            </div>
          ) : null}
          <div className="flex flex-col gap-3">
            <div className="flex flex-row items-start justify-between gap-2">
              <FormField
                name="pan"
                className="flex-1"
                render={({ field }) => (
                  <FormInput
                    placeholder="ABCDE1234J"
                    label={
                      <>
                        <span>PAN</span>&nbsp;
                        {action !== "create-account" ? (
                          <span className="text-muted-foreground">
                            (Optional)
                          </span>
                        ) : null}
                      </>
                    }
                    {...field}
                    onChange={(val) =>
                      field.onChange(val?.toLocaleUpperCase() || "")
                    }
                  />
                )}
              />
              {action === "create-account" ? (
                <div className="shrink-0 pt-5">
                  {shouldCheckKyc ? (
                    <Button
                      variant="outline"
                      disabled={isCheckingKyc || !panValue || isSubmitting}
                      type="button"
                      onClick={form.handleSubmit(handleCheckKyc)}
                    >
                      <Loader2Icon
                        className={cn("size-4 animate-spin", {
                          hidden: !isCheckingKyc,
                        })}
                      />
                      Check KYC
                    </Button>
                  ) : (
                    <KycStatusBadge
                      kyc_status={client?.kyc_status}
                      remark={client?.kyc_status_remark}
                    />
                  )}
                </div>
              ) : null}
            </div>
            {action === "create-account" && !shouldCheckKyc && (
              <KycDescription
                client={client ?? null}
                cachedName={nameChanged ? cachedClientName : undefined}
              />
            )}
          </div>
          <Activity mode={action !== "create-account" ? "visible" : "hidden"}>
            <FormField
              name="email"
              render={({ field }) => (
                <FormInput
                  label={
                    <>
                      <span>Email</span>&nbsp;
                      <span className="text-muted-foreground">(Optional)</span>
                    </>
                  }
                  {...field}
                  placeholder="Enter email address"
                />
              )}
            />
          </Activity>
          <DialogFooter className="mt-auto sm:flex-col gap-4">
            {action === "create-account" && shouldCheckKyc && (
              <div className="flex items-start gap-2 rounded-lg bg-tag-slate p-3 border border-border w-full">
                <InfoIcon className="size-4 mt-0.5 shrink-0 text-tag-slate-foreground" />
                <span className="text-xs text-tag-slate-foreground">
                  Check KYC to create the account
                </span>
              </div>
            )}
            <Button
              className="w-full"
              type="button"
              onClick={form.handleSubmit(createUpdateAccount)}
              disabled={
                isCheckingKyc ||
                isSubmitting ||
                (action === "create-account" && shouldCheckKyc)
              }
            >
              {action === "create-account" ? "Create Account" : "Update"}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
